import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  acceptConsent as acceptConsentRequest,
  changePassword as changePasswordRequest,
  login as loginRequest,
  logout as logoutRequest,
  refresh as refreshRequest,
  register as registerRequest,
  verifyMfaLogin as verifyMfaLoginRequest,
  type AcceptConsentPayload,
  type ApiClient,
  type AuthUser,
  type ChangePasswordPayload,
  type ConsentChallengeResponse,
  type LoginPayload,
  type LoginResponse,
  type LoginResult,
  type RegisterPayload,
  type VerifyMfaLoginPayload,
} from '@monecolier/api-client';
import { Button } from '../Button';
import { Card } from '../Card';
import { pageContainerClassName, secondaryTextClassName } from '../styles';

export interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  // L'instance ApiClient injectée dans <AuthProvider> — exposée telle quelle pour
  // qu'un appelant ait besoin d'un appel API supplémentaire juste après login()
  // (ex. GET /me, AUT-03, voir apps/web-admin/src/pages/Login.tsx) puisse réutiliser
  // EXACTEMENT le même client (donc le même fetchImpl, réel ou mocké en test) plutôt
  // que d'importer un second ApiClient séparément — un import séparé du singleton
  // app-level contournerait silencieusement tout ApiClient de test injecté ici.
  apiClient: ApiClient;
  // Renvoie le LoginResult (accessToken inclus SI la connexion est complète, en plus
  // de mettre à jour l'état du contexte dans ce cas) — AUT-03 : un appelant qui a
  // besoin du token IMMÉDIATEMENT après un login réussi (ex. web-admin, pour
  // enchaîner un appel GET /me avant de décider où naviguer) ne peut pas se fier à
  // `accessToken` relu via `useAuth()` juste après — la mise à jour d'état React
  // n'est pas garantie visible dans le même tick (closure périmée). Le retour
  // explicite évite ce piège. ⚠️ AUT-05 : le résultat peut désormais être un DÉFI
  // MFA (`{ mfaRequired: true, mfaToken }`, AUCUN token émis, état du contexte
  // INCHANGÉ) — l'appelant DOIT tester `'mfaRequired' in result` avant de lire
  // `accessToken`/`user` (voir apps/web-admin/src/pages/Login.tsx) ; web-parent, qui
  // n'inspecte jamais le retour de `login()`, n'a rien à changer (aucun compte MFA
  // n'y existe structurellement).
  login: (payload: LoginPayload) => Promise<LoginResult>;
  // AUT-05 — second temps de la connexion après un défi MFA : `code` (TOTP ou
  // secours) + le `mfaToken` reçu de `login()`. Met à jour l'état du contexte SEULEMENT
  // si la connexion est désormais complète — CMP-02 : le résultat peut être un DÉFI DE
  // CONSENTEMENT à la place (`{ consentRequired: true, consentToken }`, ÉTAT DU
  // CONTEXTE INCHANGÉ) — l'appelant DOIT tester `'consentRequired' in result` avant de
  // lire `accessToken`/`user`, même discipline que `login()` pour `mfaRequired`.
  verifyMfa: (payload: VerifyMfaLoginPayload) => Promise<LoginResponse | ConsentChallengeResponse>;
  // CMP-02 — second temps de la connexion après un DÉFI DE CONSENTEMENT (reçu de
  // `login()` OU `verifyMfa()`) : `consentToken` transmis tel quel. Complète TOUJOURS
  // la connexion (jamais un second défi) — met à jour l'état du contexte comme
  // `login()`/`verifyMfa()` le font pour une connexion sans défi.
  acceptConsent: (payload: AcceptConsentPayload) => Promise<LoginResponse>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => void;
  // AUT-16 — change son mot de passe DEPUIS un compte DÉJÀ CONNECTÉ (DISTINCT du
  // flux `resetPassword()` par lien email, qui vit hors de ce contexte car il
  // n'authentifie jamais automatiquement). Le backend RÉ-ÉMET une session
  // fraîche (nouveau `tv`, voir apps/api/src/routes/auth.ts) — nécessaire pour
  // que la session COURANTE reste valide malgré la révocation de toutes les
  // AUTRES ; sans mettre à jour `accessToken`/`user` ici, le prochain appel
  // authentifié de CETTE app utiliserait encore l'ANCIEN token (état React
  // périmé) et échouerait en 401 dès le changement effectué — même piège que
  // `login()`/`verifyMfa()`/`acceptConsent()` ci-dessus, corrigé de la même
  // façon.
  changePassword: (payload: ChangePasswordPayload) => Promise<LoginResponse>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export interface AuthProviderProps {
  apiClient: ApiClient;
  children: ReactNode;
  // AUT-06 : affiché à la place de `children` pendant la restauration de session
  // proactive au montage (voir plus bas) — évite de flasher le formulaire de
  // connexion puis le contenu protégé (l'inverse serait pire : flasher du contenu
  // protégé avant de découvrir que la session n'est en fait pas valide). Par défaut,
  // un texte minimal suffisant pour les deux apps — personnalisable si une app a
  // besoin d'un habillage différent, mais aucune des deux n'en a besoin aujourd'hui.
  loadingFallback?: ReactNode;
}

const DEFAULT_LOADING_FALLBACK = (
  <p className={secondaryTextClassName} role="status">
    Chargement…
  </p>
);

// AUT-30 — remplace ENTIÈREMENT `children` (donc aussi <BrowserRouter>, monté par
// chaque app AUTOUR de <AuthProvider>, voir App.tsx de web-admin/web-parent) tant que
// la session est déclarée expirée — jamais le message brut "Authentification
// requise" laissé sans action possible. Pas de navigation programmatique possible
// depuis ici (`useNavigate()` exige un Router, absent à ce niveau) : le bouton se
// contente de refermer cet écran — l'état d'authentification étant déjà nettoyé
// (voir `onSessionExpired` plus bas), l'app qui réapparaît alors affiche
// NATURELLEMENT son propre écran "non connecté" (Login, PublicSchoolLanding...) via
// les gardes déjà en place page par page, sans dupliquer ici un chemin `/login`
// différent par app.
function SessionExpiredScreen({ onReconnect }: { onReconnect: () => void }) {
  return (
    <div className={pageContainerClassName}>
      <Card>
        <p className="text-base font-semibold text-text" role="alert" data-testid="session-expired-message">
          Votre session a expiré.
        </p>
        <p className={secondaryTextClassName}>
          Pour votre sécurité, vous avez été déconnecté(e) après une période d&apos;inactivité. Reconnectez-vous pour
          continuer.
        </p>
        <Button type="button" onClick={onReconnect} data-testid="session-expired-reconnect">
          Se reconnecter
        </Button>
      </Card>
    </div>
  );
}

/**
 * Authentification partagée entre web-admin et web-parent (AUT-01, extension
 * frontend) — logique 100% identique entre les deux apps (mêmes routes /auth/*,
 * même forme de réponse) : factorisée ici plutôt que dupliquée deux fois, voir
 * CLAUDE.md, "Workflow Git"/conventions du monorepo.
 *
 * ⚠️ L'access token vit UNIQUEMENT en mémoire (`useState` ci-dessous) — jamais dans
 * `localStorage`/`sessionStorage`, lisibles par n'importe quel script injecté en cas
 * de XSS (décision AUT-01, voir docs/architecture.md). Un rechargement de page perd
 * donc TOUJOURS la mémoire JS — mais PAS la session : voir la restauration proactive
 * ci-dessous (AUT-06).
 *
 * ⚠️ AUT-06 — BUG CORRIGÉ : jusqu'à ce ticket, `<AuthProvider>` ne tentait JAMAIS de
 * restaurer la session au montage (`POST /auth/refresh` n'existait même pas côté
 * backend, voir apps/api/src/routes/auth.ts) — un simple F5 forçait donc TOUJOURS
 * une reconnexion complète, même avec un refresh token cookie httpOnly parfaitement
 * valide. Corrigé par l'appel `refreshRequest()` dans le `useEffect` ci-dessous,
 * déclenché au montage, PROACTIVEMENT — pas seulement de façon réactive sur un 401
 * d'un appel métier quelconque (qui laisserait l'utilisateur visuellement
 * non-authentifié jusqu'au premier appel qui échoue). Le refresh token, lui, vit
 * dans un cookie httpOnly émis par le serveur : rien à lire/transmettre ici, le
 * navigateur l'envoie automatiquement (`credentials: 'include'`, voir
 * `ApiClient.request`). Pendant cette restauration (`isRestoring`), `children` n'est
 * PAS rendu — voir `loadingFallback` ci-dessus — pour éviter tout scintillement
 * (formulaire de connexion affiché puis aussitôt remplacé par le contenu protégé,
 * ou l'inverse).
 *
 * `register()` n'authentifie PAS automatiquement (POST /auth/register ne renvoie
 * aucun token côté backend, par construction) — c'est au composant appelant
 * (`Register.tsx` de chaque app) d'enchaîner explicitement avec `login()` s'il veut
 * connecter l'utilisateur immédiatement après l'inscription.
 */
export function AuthProvider({ apiClient, children, loadingFallback = DEFAULT_LOADING_FALLBACK }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // AUT-06 : `true` jusqu'à ce que la tentative de restauration proactive (succès OU
  // échec — un refresh token absent/expiré est un résultat NORMAL, pas une erreur)
  // se termine. Volontairement PAS exposé dans `AuthContextValue` : la gestion de cet
  // état reste un détail d'implémentation de `<AuthProvider>` (voir `loadingFallback`
  // ci-dessus), aucun composant appelant n'a eu besoin d'y réagir individuellement
  // jusqu'ici — à exposer le jour où un besoin réel se présente, pas par anticipation.
  const [isRestoring, setIsRestoring] = useState(true);
  // AUT-30 — `true` UNIQUEMENT quand un rafraîchissement silencieux EN COURS DE
  // SESSION (déclenché par ApiClient sur un 401, voir setAuthRecoveryHooks
  // ci-dessous) a lui-même échoué : refresh token expiré/révoqué. DISTINCT
  // d'`isRestoring` (restauration proactive au MONTAGE, AUT-06) — les deux ne
  // peuvent jamais être vrais en même temps (l'un précède toujours l'autre dans le
  // temps), mais gérés séparément pour ne jamais réafficher `loadingFallback`
  // (transitoire, quelques centaines de ms) à la place de cet écran bien plus
  // délibéré.
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function restore() {
      try {
        const result = await refreshRequest(apiClient);
        if (cancelled) return;
        setAccessToken(result.accessToken);
        setUser(result.user);
      } catch {
        // 401 attendu (pas de cookie, cookie expiré, session révoquée...) — état
        // NON authentifié, silencieux : jamais une erreur affichée à l'utilisateur
        // pour ce cas, qui est le comportement normal d'un premier visiteur ou d'un
        // utilisateur déconnecté depuis longtemps.
      } finally {
        if (!cancelled) setIsRestoring(false);
      }
    }

    void restore();

    return () => {
      cancelled = true;
    };
    // `apiClient` est stable pour la durée de vie du provider (voir la remarque sur
    // `apiClient` dans AuthContextValue ci-dessus) — un seul appel au montage,
    // jamais reconduit sur un re-render, même si l'appelant recréait `apiClient` à
    // chaque rendu (ne devrait jamais arriver en pratique, mais volontaire quand
    // même : cet effet ne doit tourner qu'UNE fois). FND-21 :
    // `react-hooks/exhaustive-deps` réactivée (eslint-plugin-react-hooks 7.1.1) —
    // suppression CIBLÉE ici, intention documentée plutôt qu'une désactivation
    // globale de la règle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // AUT-30 — appelée par ApiClient (via setAuthRecoveryHooks juste en dessous)
  // suite à un 401 EN COURS DE SESSION, sur une requête qui portait déjà un
  // token : tente le MÊME POST /auth/refresh que la restauration proactive
  // ci-dessus (AUT-06, RÉUTILISÉ, jamais dupliqué), mais AILLEURS QUE le montage —
  // c'est précisément le trou que ce ticket comble : passé 15 min (ACCESS_TOKEN_TTL,
  // apps/api/src/lib/jwt.ts) sur un même écran sans rechargement, plus rien ne
  // rattrapait le prochain appel. Renvoie le nouveau token pour qu'ApiClient rejoue
  // la requête d'origine avec — ET met à jour l'état du contexte pour que tout
  // AUTRE composant qui enverrait ensuite l'ANCIEN token explicitement
  // (`RequestOptions.accessToken`, AUT-03) obtienne le NOUVEAU dès son prochain
  // rendu, sans devoir lui-même re-échouer en 401 pour le découvrir. Rejette tel
  // quel si `refreshRequest` échoue (refresh token expiré/révoqué) — ApiClient
  // interprète ce rejet comme « le rafraîchissement a échoué », voir
  // AuthRecoveryHooks#refresh dans ApiClient.ts.
  const refreshSilently = useCallback(async (): Promise<string> => {
    const result = await refreshRequest(apiClient);
    setAccessToken(result.accessToken);
    setUser(result.user);
    return result.accessToken;
  }, [apiClient]);

  // AUT-30 — `refreshSilently()` ci-dessus a ÉCHOUÉ : la session est RÉELLEMENT
  // terminée. Nettoie l'état d'authentification (comme `logout()` plus bas, mais
  // SANS son appel best-effort à POST /auth/logout — le refresh a déjà échoué,
  // aucun cookie valide à invalider côté serveur) et bascule sur
  // <SessionExpiredScreen> (voir le rendu tout en bas), qui REMPLACE `children` —
  // jamais le message brut "Authentification requise" laissé sans action possible.
  const onSessionExpired = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setSessionExpired(true);
  }, []);

  // AUT-30 — branche les deux callbacks ci-dessus sur l'instance ApiClient : celle-ci
  // est un singleton créé AVANT React (voir apps/*/src/services/api.ts), donc AVANT
  // qu'aucun `setAccessToken`/`setUser` n'existe pour l'alimenter — d'où ce
  // branchement tardif plutôt qu'un paramètre du constructeur. `apiClient` étant
  // stable pour la durée de vie du provider (voir le commentaire sur `apiClient`
  // dans AuthContextValue), et `refreshSilently`/`onSessionExpired` mémorisés sur des
  // dépendances elles-mêmes stables, cet effet ne s'exécute qu'une fois — débranché
  // au démontage pour ne jamais laisser une instance orpheline invoquer un
  // `setState` sur un provider disparu.
  useEffect(() => {
    apiClient.setAuthRecoveryHooks({ refresh: refreshSilently, onSessionExpired });
    return () => {
      apiClient.setAuthRecoveryHooks(null);
    };
  }, [apiClient, refreshSilently, onSessionExpired]);

  // AUT-30 — dépasse <SessionExpiredScreen>, voir son commentaire de tête : l'état
  // d'authentification est déjà nettoyé par `onSessionExpired()` ci-dessus, cette
  // fonction ne fait donc que refermer l'écran, révélant `children` (donc l'app,
  // désormais non-authentifiée) à nouveau.
  const dismissSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const login = useCallback(
    async (payload: LoginPayload) => {
      const result = await loginRequest(apiClient, payload);
      // AUT-05 : un défi MFA n'authentifie PAS encore l'utilisateur — état du
      // contexte volontairement INCHANGÉ, l'appelant doit compléter via
      // `verifyMfa()` avant que `accessToken`/`user` ne soient jamais posés.
      // CMP-02 : même principe pour un défi de consentement — INCHANGÉ tant que
      // `acceptConsent()` n'a pas été appelée.
      if ('mfaRequired' in result || 'consentRequired' in result) return result;
      setAccessToken(result.accessToken);
      setUser(result.user);
      return result;
    },
    [apiClient],
  );

  const verifyMfa = useCallback(
    async (payload: VerifyMfaLoginPayload) => {
      const result = await verifyMfaLoginRequest(apiClient, payload);
      // CMP-02 : voir le commentaire équivalent sur `login()` ci-dessus — un défi de
      // consentement APRÈS un MFA réussi n'authentifie TOUJOURS PAS l'utilisateur.
      if ('consentRequired' in result) return result;
      setAccessToken(result.accessToken);
      setUser(result.user);
      return result;
    },
    [apiClient],
  );

  const acceptConsent = useCallback(
    async (payload: AcceptConsentPayload) => {
      const result = await acceptConsentRequest(apiClient, payload);
      setAccessToken(result.accessToken);
      setUser(result.user);
      return result;
    },
    [apiClient],
  );

  // AUT-16 — voir le commentaire sur `changePassword` dans `AuthContextValue`
  // ci-dessus : appelable seulement une fois authentifié (`accessToken` non
  // `null`), l'appelant (l'écran "Changer mon mot de passe") ne devrait jamais
  // atteindre ce point sans session déjà établie — un jeton absent lève une
  // erreur explicite plutôt qu'un appel réseau silencieusement mal formé.
  const changePassword = useCallback(
    async (payload: ChangePasswordPayload) => {
      if (!accessToken) {
        throw new Error('changePassword() appelé sans session active.');
      }
      const result = await changePasswordRequest(apiClient, payload, accessToken);
      setAccessToken(result.accessToken);
      setUser(result.user);
      return result;
    },
    [apiClient, accessToken],
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      await registerRequest(apiClient, payload);
    },
    [apiClient],
  );

  // AUT-06 : contrairement à l'ancien `logout()` (effaçait UNIQUEMENT la mémoire JS
  // locale), invalide RÉELLEMENT le cookie httpOnly côté serveur via POST
  // /auth/logout — sans cet appel, un F5 juste après un "logout" aurait
  // silencieusement reconnecté l'utilisateur (le cookie, lui, restait valide 7
  // jours). État local effacé IMMÉDIATEMENT (avant même la résolution de l'appel
  // réseau) : l'UI ne doit pas attendre un aller-retour serveur pour refléter la
  // déconnexion — l'appel réseau, best-effort, tourne en arrière-plan (même
  // philosophie que l'email de confirmation de AUT-02 : la partie critique côté
  // client — ici, l'état local — est déjà faite avant). Signature synchrone
  // inchangée (`() => void`) : tous les appelants existants (boutons "Se
  // déconnecter" de web-admin/web-parent) l'invoquent déjà sans `await`.
  const logout = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    void logoutRequest(apiClient).catch(() => {
      // Échec réseau best-effort — l'état local est déjà propre, rien de plus à
      // faire ici ; un cookie qui survivrait malgré cet échec expirera de
      // toute façon naturellement (7 jours, voir REFRESH_TOKEN_TTL côté backend).
    });
  }, [apiClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      isAuthenticated: user !== null,
      apiClient,
      login,
      verifyMfa,
      acceptConsent,
      register,
      logout,
      changePassword,
    }),
    [user, accessToken, apiClient, login, verifyMfa, acceptConsent, register, logout, changePassword],
  );

  return (
    <AuthContext.Provider value={value}>
      {isRestoring ? (
        loadingFallback
      ) : sessionExpired ? (
        <SessionExpiredScreen onReconnect={dismissSessionExpired} />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth() doit être appelé à l\'intérieur de <AuthProvider>.');
  }
  return context;
}
